// The QR-dining → captain-flow API client.
//
// A FACTORY, NOT A MODULE OF BARE FUNCTIONS. The fetch transports and the base
// URL are injected so every endpoint's exact request is assertable in tests
// with no server running (same convention as the counter dashboard's
// counterBillingApi.js on the agent_web_app_captain_dashboard branch).
//
// THIS IS THE ONLY PLACE A CAPTAIN URL OR BODY SHAPE IS CONSTRUCTED. Nothing
// else in the QR-captain feature builds one.
//
// Three properties of the shared transports that dictate this file's shape:
//
// 1. post() TAKES A PRE-STRINGIFIED BODY. postWithAuthCallWithErrorResponse
//    passes requestBody straight to fetch({ body }) with an application/json
//    Content-Type and does NOT stringify — an object would be sent as the
//    literal string "[object Object]". Hence JSON.stringify on EVERY write,
//    even the bodyless cancel.
//
// 2. THEY RETURN AN ENVELOPE AND DO NOT THROW ON HTTP ERRORS. Both resolve
//    { response, json, error: !response.ok } — a 409 is a resolved value.
//
// 3. THEY *DO* REJECT ON NETWORK FAILURE AND ON A NON-JSON BODY (response.json()
//    is called unconditionally). An unhandled rejection inside a poll tick kills
//    the tick silently, so every call here is normalised to the same envelope
//    and NOTHING this client returns ever rejects.
//
// AUTH: the transports send the app's own `Authorization: jwt <Token>` header —
// the captain routes accept the logged-in customer's token (no role check).

import ApiConfig from "./ApiConfig";
import {
  simpleGetCallWithErrorResponse,
  postWithAuthCallWithErrorResponse,
} from "./ApiServices";

/**
 * The normalised result every method resolves to.
 *
 * @typedef {Object} CaptainResult
 * @property {boolean} ok           HTTP 2xx AND a parseable body
 * @property {number}  status       HTTP status, or 0 when the request never landed
 * @property {any}     data         json.data — the payload (every endpoint wraps in {success, data})
 * @property {string}  message      json.message — safe to render verbatim
 * @property {any}     json         the whole parsed body, for anything unforeseen
 * @property {boolean} networkError true when the request never reached the server
 */
function unwrap(envelope) {
  const response = envelope && envelope.response;
  const json = (envelope && envelope.json) || {};
  return {
    ok: !!(response && response.ok),
    status: (response && response.status) || 0,
    data: json.data === undefined ? null : json.data,
    message: typeof json.message === "string" ? json.message : "",
    json,
    networkError: false,
  };
}

// A request that never reached the server (or returned a non-JSON body, e.g. a
// proxy 502). status 0 is deliberately not any HTTP status: callers keep their
// last-known data and retry quietly instead of misreading it as a 404.
function asFailure(err) {
  return {
    ok: false,
    status: 0,
    data: null,
    message: "Couldn't reach the server. Please check your connection.",
    json: null,
    networkError: true,
    error: err,
  };
}

/**
 * Expired-session detector, applied centrally by callers before any other error
 * handling. The backend returns HTTP 400 (not 401) with a message matching
 * /token is expired/i for an expired JWT; the 401 variants (user missing /
 * inactive / bad signature) also mean "this session cannot continue".
 */
export function isSessionExpired(res) {
  if (!res) return false;
  if (res.status === 401) return true;
  return res.status === 400 && /token is expired/i.test(res.message || "");
}

export function createCaptainApi({
  baseUrl = ApiConfig.BASE_URL,
  get = simpleGetCallWithErrorResponse,
  post = postWithAuthCallWithErrorResponse,
} = {}) {
  const base = `${baseUrl}/api/v1/captain`;
  const wrap = (promise) => promise.then(unwrap).catch(asFailure);

  return {
    /**
     * Resolve the QR URL's table label + optional area STRINGS into the numeric
     * table_id every other captain endpoint requires.
     * 200 → data {table_id, table_no, area_name, is_active}
     * 404 table not found · 409 ambiguous (needs area) — both mean "fall back
     * to the normal dine flow".
     */
    resolveTable({ shop_id, table_no, area } = {}) {
      const params = new URLSearchParams({
        shop_id: String(shop_id),
        table_no: String(table_no),
      });
      if (area != null && String(area).trim() !== "") {
        params.set("area", String(area));
      }
      return wrap(get(`${base}/tables/resolve?${params.toString()}`));
    },

    /**
     * Fire one KOT round to the kitchen. Opens the tab on the first round,
     * appends on later rounds. `idempotencyKey` must be a FRESH uuid per tap —
     * retrying the same key replays the same round instead of duplicating it.
     * 200 → data {order_id, kot_no, is_new_order, running_total}
     * 400 out of stock (show message verbatim) · 409 tab already closed.
     */
    postKot(tableId, body, idempotencyKey) {
      const headers = idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {};
      return wrap(
        post(`${base}/tables/${tableId}/kot`, JSON.stringify(body), headers)
      );
    },

    /**
     * The live bill for the table's open tab (primary poll on My Table).
     * 404 "No running order on this table" after the counter settles — combined
     * with a saved order id that means the settled phase.
     */
    getTableBill(tableId) {
      return wrap(get(`${base}/tables/${tableId}/bill`));
    },

    /** The tab's KOT rounds → data {order_id, kots: [{kot_no, placed_at, items}]}. */
    getTableKots(tableId) {
      return wrap(get(`${base}/tables/${tableId}/kots`));
    },

    /**
     * The same bill addressed by ORDER id — still works after the table is
     * released, so it renders the settled/thank-you screen. Its data also
     * carries `bill_request` (the pending request row or null), used to
     * rehydrate the billRequested phase.
     */
    getOrderBill(orderId) {
      return wrap(get(`${base}/orders/${orderId}/bill`));
    },

    /**
     * Request the bill (pay at the counter). 200 → data includes
     * {bill_request_id, created} — created:false on a double-tap is still a
     * success; adopt the returned id. 409 = multiple open orders (ask staff).
     */
    generateBill(tableId, { source = "qr" } = {}) {
      return wrap(
        post(`${base}/tables/${tableId}/generate-bill`, JSON.stringify({ source }))
      );
    },

    /** Withdraw a pending bill request (guest wants another round). */
    cancelBillRequest(billRequestId) {
      return wrap(
        post(`${base}/bill-requests/${billRequestId}/cancel`, JSON.stringify({}))
      );
    },
  };
}

// The app-wide instance every screen uses. Tests build their own via the factory.
const captainApi = createCaptainApi();
export default captainApi;
